import { getUuidV4 } from '../../../utilities/generic-utilities';
import { AddressState, initialAddressState, TimesheetAddressState } from '../../address';
import { findKnownAddress, upsertAddress } from '../../address/address-merge';
import { manualAddrNumber } from '../../address/address-number';
import { TimesheetState } from '../timesheet-collection.state';

/** What {@link normalizeStoredEntryAddresses} hands back. */
export type NormalizedEntryAddresses = {
    timesheets: TimesheetState[];
    addresses: AddressState[];
    changed: boolean;
};

/**
 * The shape a timesheet entry's `address` field may actually hold once read back from storage: a
 * plain id, `null` — what the current type says — or, for an entry written before this refactor,
 * the full address projection `entry.address` used to be typed as.
 *
 * `entry.address` is now `string | null`, so recognizing the legacy object shape needs a deliberate
 * widening back to what storage may actually hold: storage sits outside the type's guarantees, and
 * a device that has not opened the app since before this change still holds data the current type
 * does not describe. `setTimesheetsReducer` restores its own invariants on the way in for the same
 * reason.
 */
type PersistedEntryAddress = string | TimesheetAddressState | null;

/**
 * Narrows a persisted entry address to the legacy full-object shape.
 *
 * @param value the entry's `address` field, widened to what storage may actually hold
 */
const isLegacyStoredAddress = (value: PersistedEntryAddress): value is TimesheetAddressState =>
    typeof value === 'object' && value !== null;

/**
 * Whether any entry in the collection still holds the legacy full-object address. Cheap enough to
 * run before deciding whether normalization has to wait for the address slice at all — see
 * `hydrateTimesheet$`, which must not gain a dependency on that slice for the common case of a
 * hydrate with nothing to normalize.
 *
 * @param timesheets the timesheets as hydrated from storage, before normalization
 */
export function hasLegacyStoredAddresses(timesheets: readonly TimesheetState[]): boolean {
    // No narrowing needed here — only the boolean result matters — so `entry.address` (`string |
    // null`) widens into the guard's `PersistedEntryAddress` parameter by ordinary assignability,
    // with no assertion required.
    return timesheets.some((timesheet) => timesheet.entries.some((entry) => isLegacyStoredAddress(entry.address)));
}

/**
 * Resolves one legacy address projection against the working collection, minting and folding in a
 * new address when nothing already known stands for it.
 *
 * Matching and folding go through `findKnownAddress` / `upsertAddress` — the same rules the address
 * slice itself applies for "is this the same address" — rather than a second notion of it invented
 * here.
 *
 * @param stored the legacy address projection an entry held
 * @param addresses the working address collection, threaded through so two entries naming the same
 *     place resolve to the same address rather than minting one each
 * @returns the id the entry should now hold, and the collection it resolves against
 */
function resolveLegacyAddress(
    stored: TimesheetAddressState,
    addresses: readonly AddressState[],
): { id: string; addresses: readonly AddressState[] } {
    const candidate: AddressState = { ...initialAddressState, ...stored };
    const match = findKnownAddress(candidate, addresses);
    if (match) {
        return { id: match.id, addresses };
    }

    // No backend addrNumber survives a stored projection's round trip through the legacy shape any
    // better than a hand-entered address's does, so a missing one is minted exactly the way the
    // address picker mints one for a hand-entered address.
    const id = getUuidV4();
    const minted: AddressState = {
        ...candidate,
        id,
        addrNumber: candidate.addrNumber || manualAddrNumber(id),
    };
    return { id, addresses: upsertAddress(addresses, minted) };
}

/**
 * Normalizes timesheets rehydrated from storage: replaces a legacy full-object stored address with
 * the id of the corresponding address in the address slice, creating that address — folded into the
 * returned collection — when the slice does not already hold it. An entry whose stored address is
 * already an id, or `null`, passes through untouched.
 *
 * A stored projection that is not usable as an address at all (no free text, no street and city) is
 * still created rather than dropped: losing the entry's only link to what address it was recorded
 * against is worse than the address book holding one blank entry.
 *
 * `addresses` must already reflect the slice's `'ready'` collection — see `hydrateTimesheet$`, which
 * gates on that before calling this; run against an empty or partial collection, every stored
 * address looks unknown and each gets minted as a duplicate.
 *
 * @param timesheets the timesheets as hydrated from storage
 * @param addresses the address collection to resolve and mint against
 * @returns the normalized timesheets, the address collection with any minted addresses folded in,
 *     and whether anything actually changed. `false` returns both inputs back by reference — not
 *     merely by value — so a caller can tell "nothing to do" apart from "produced an equal result"
 *     and dispatch nothing rather than looping on its own write.
 */
export function normalizeStoredEntryAddresses(
    timesheets: readonly TimesheetState[],
    addresses: readonly AddressState[],
): NormalizedEntryAddresses {
    if (!hasLegacyStoredAddresses(timesheets)) {
        return { timesheets: timesheets as TimesheetState[], addresses: addresses as AddressState[], changed: false };
    }

    let workingAddresses = addresses;
    let changed = false;

    const normalizedTimesheets = timesheets.map((timesheet) => {
        let entriesChanged = false;
        const entries = timesheet.entries.map((entry) => {
            const stored = entry.address as PersistedEntryAddress;
            if (!isLegacyStoredAddress(stored)) {
                return entry;
            }
            const resolved = resolveLegacyAddress(stored, workingAddresses);
            workingAddresses = resolved.addresses;
            entriesChanged = true;
            return { ...entry, address: resolved.id };
        });
        if (!entriesChanged) {
            return timesheet;
        }
        changed = true;
        return { ...timesheet, entries };
    });

    return {
        timesheets: changed ? normalizedTimesheets : (timesheets as TimesheetState[]),
        addresses: workingAddresses as AddressState[],
        changed,
    };
}
